import type { ReactNode } from "react";
import { styled } from "styled-components";

import { UIButton } from "common/components";
import { CrossIcon, LeftArrow } from "common/components/icons";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTranslate } from "common/utilities/useTranslate";
import { deactivateModal, popModalScreen } from "domains/modal/actions";
import { getActiveModals, getModalParams } from "domains/modal/selectors";
import type { ModalContentType, ModalParams } from "domains/modal/types";

const StyledTitleContainer = styled.div<{ rightAlign: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ rightAlign }) => (rightAlign ? "flex-end" : "space-between")};
  margin-bottom: 3rem;
  padding: 1.5rem 0;
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme.colors.textBright}0F; // 1/16 opacity

  @media (max-height: 480px) {
    margin-bottom: 1.5rem;
  }
`;

const StyledModalTitle = styled.h2`
  margin: 0;
  font-weight: bold;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledCrossIcon = styled(CrossIcon)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.textFade};
  transition: all 0.3s;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
`;

const StyledTitleRow = styled(StyledRow)`
  display: flex;
  flex-direction: row;
  column-gap: 0.5rem;
`;

const StyledButton = styled(UIButton)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background: none;
  aspect-ratio: 1 / 1;
  padding: 10px;

  &:focus-visible,
  &:hover {
    > svg {
      color: ${({ theme }) => theme.colors.textBright};
    }
    background: ${({ theme }) => theme.colors.modalCardBackground};
  }
`;

const StyledChevron = styled(LeftArrow)`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.colors.textBright};
`;

type ModalHeaderProps = { className?: string; popOnDismiss?: boolean; rightElement?: ReactNode; title?: string };

export const ModalHeader = ({ className, popOnDismiss, rightElement, title }: ModalHeaderProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const translate = useTranslate();

  const params = useSelector(getModalParams) as ModalParams<ModalContentType>;
  const activeModals = useSelector(getActiveModals);

  const displayBackButton = activeModals.length > 1;

  const dismiss = () => {
    dispatch(popOnDismiss ? popModalScreen() : deactivateModal());
    if (params.onDismissRoute !== undefined) go(params.onDismissRoute);
  };

  const handlePopModal = () => dispatch(popModalScreen());

  const closeText = translate("app.dialog.close");

  return (
    <StyledTitleContainer rightAlign={!title} className={className}>
      <StyledTitleRow>
        {displayBackButton && (
          <StyledButton
            onClick={displayBackButton ? handlePopModal : undefined}
            aria-label={translate("app.navigation.back")}
            title={translate("app.navigation.back")}
          >
            <StyledChevron />
          </StyledButton>
        )}
        {title && <StyledModalTitle data-test-id="styled-modal-title">{title}</StyledModalTitle>}
      </StyledTitleRow>
      <StyledRow>
        {rightElement}
        <StyledButton onClick={dismiss} aria-label={closeText} title={closeText}>
          <StyledCrossIcon />
        </StyledButton>
      </StyledRow>
    </StyledTitleContainer>
  );
};
